import ExceptionCode from '@/Enums/Exceptions/ExceptionCode.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import Dv360ReportType from '@/Enums/ReportingDashboard/Dv360ReportType.enum';
import { REPORTING_GOOGLE_QUERY_ENDPOINT, REPORTING_GOOGLE_REPORT_ENDPOINT } from '@/constants/endpoints';
import {
  GOOGLE_MULTI_WHITELABEL_MESSAGE,
  getEffectiveFilters,
  isGoogleMultiWhitelabelSelection,
} from '@/helpers/reportingDashboard/filters';
import {
  selectReportingDashboardCurrentApiTabClients,
  selectReportingDashboardCurrentApiTabWhitelabels,
} from '@/store/selectors/reportingDashboard/shared.selectors';
import axios, { HttpStatusCode } from 'axios';
import { getAsyncGoogleResults } from '../axios';
import { isRoleAllowedWhitelabelSelect } from '../roles';
import { getGoogleReportingFilter } from './filters';
import { mapReportWithLogos } from './slice';

const getFallbackValues = filters => ({ report: [], totals: {}, filters });

const DEFAULT_TIMEOUT = 5000;

const shouldReturnFallbackForSelection = (role, whitelabels, clients) => {
  if (isRoleAllowedWhitelabelSelect(role)) {
    return !whitelabels?.length;
  }
  return !clients?.length;
};

export const sdkReportRequestHandler =
  (reportType, options) =>
  async (payload, { getState }) => {
    const { timeout } = options ?? {};
    const state = getState();

    const {
      me: { role },
    } = state;

    const filters = getEffectiveFilters(state, ApiTabIndex.Google);

    const whitelabels = selectReportingDashboardCurrentApiTabWhitelabels(state);
    const clients = selectReportingDashboardCurrentApiTabClients(state);
    const googleFilters = getGoogleReportingFilter(filters, role, clients, whitelabels);

    const fallbackValues = getFallbackValues(filters);

    if (shouldReturnFallbackForSelection(role, whitelabels, clients)) {
      return fallbackValues;
    }

    // flank#4689: a multi-select silently falls back to the viewer's own Whitelabel
    // (see getGoogleReportingFilter) — tell the user instead of reporting on it.
    if (isGoogleMultiWhitelabelSelection(filters)) {
      return { ...fallbackValues, errorCardMessage: GOOGLE_MULTI_WHITELABEL_MESSAGE };
    }

    if (!googleFilters.advertiserIds?.length && !googleFilters.campaignIds?.length) {
      return fallbackValues;
    }

    let responseQuery;

    try {
      responseQuery = await axios.post(
        REPORTING_GOOGLE_QUERY_ENDPOINT,
        { filters: googleFilters, reportType },
        { signal: payload?.signal },
      );
    } catch (error) {
      if (
        error?.response?.status === HttpStatusCode.UnprocessableEntity &&
        error?.response?.data?.code === ExceptionCode.NotFound
      ) {
        return fallbackValues;
      }
      throw error;
    }

    const { queryId, reportId } = responseQuery?.data || {};

    const responseResult = await getAsyncGoogleResults({
      queryId,
      reportId,
      reportType,
      signal: payload?.signal,
      timeout: timeout ?? DEFAULT_TIMEOUT,
    });
    return { ...responseResult.data, filters };
  };

export const reportRequestHandler =
  reportType =>
  async (payload, { getState }) => {
    const state = getState();
    const {
      me: { role },
    } = state;

    const filters = getEffectiveFilters(state, ApiTabIndex.Google);

    const whitelabels = selectReportingDashboardCurrentApiTabWhitelabels(state);
    const clients = selectReportingDashboardCurrentApiTabClients(state);
    const googleFilters = getGoogleReportingFilter(filters, role, clients, whitelabels);
    const fallbackValues = getFallbackValues(filters);

    if (shouldReturnFallbackForSelection(role, whitelabels, clients)) {
      return fallbackValues;
    }

    // flank#4689: a multi-select silently falls back to the viewer's own Whitelabel
    // (see getGoogleReportingFilter) — tell the user instead of reporting on it.
    if (isGoogleMultiWhitelabelSelection(filters)) {
      return { ...fallbackValues, errorCardMessage: GOOGLE_MULTI_WHITELABEL_MESSAGE };
    }

    if (!googleFilters.advertiserIds?.length && !googleFilters.campaignIds?.length) {
      return fallbackValues;
    }

    const reportResponse = await axios.post(
      REPORTING_GOOGLE_REPORT_ENDPOINT,
      { filters: googleFilters, reportType },
      { signal: payload?.signal },
    );

    return { ...reportResponse.data, filters };
  };

export const youtubeReportRequestHandler =
  reportType =>
  async (payload, { getState }) => {
    const state = getState();
    const {
      reportingDashboardGoogle: { campaignData },
      me: { role },
    } = state;

    const appliedFilters = getEffectiveFilters(state, ApiTabIndex.Google);
    const fallbackValues = getFallbackValues(appliedFilters);

    if (!campaignData?.length) {
      return fallbackValues;
    }

    const whitelabels = selectReportingDashboardCurrentApiTabWhitelabels(state);
    const clients = selectReportingDashboardCurrentApiTabClients(state);

    const { advertiserIds, ...googleFilters } = getGoogleReportingFilter(appliedFilters, role, clients, whitelabels);

    if (!googleFilters.campaignIds?.length) {
      // youtube reports don't support advertisers as a filter
      googleFilters.campaignIds = campaignData.map(campaign => campaign[BeeswaxReportProperties.CAMPAIGN_ID]);
    }

    const reportResponse = await axios.post(
      REPORTING_GOOGLE_REPORT_ENDPOINT,
      {
        filters: googleFilters,
        reportType,
      },
      {
        signal: payload?.signal,
      },
    );

    return { ...reportResponse.data, filters: appliedFilters };
  };

export const youtubePublishersReportRequestHandler = async (payload, { getState }) => {
  const state = getState();
  const {
    reportingDashboardGoogle: { campaignData },
    me: { role },
  } = state;

  const appliedFilters = getEffectiveFilters(state, ApiTabIndex.Google);
  const fallbackValues = getFallbackValues(appliedFilters);

  if (!campaignData?.length) {
    return fallbackValues;
  }

  const whitelabels = selectReportingDashboardCurrentApiTabWhitelabels(state);
  const clients = selectReportingDashboardCurrentApiTabClients(state);

  const { advertiserIds, ...googleFilters } = getGoogleReportingFilter(appliedFilters, role, clients, whitelabels);

  if (!googleFilters.campaignIds?.length) {
    // youtube reports don't support advertisers as a filter
    googleFilters.campaignIds = campaignData.map(campaign => campaign[BeeswaxReportProperties.CAMPAIGN_ID]);
  }

  const reportResponse = await axios.post(
    REPORTING_GOOGLE_REPORT_ENDPOINT,
    { filters: googleFilters, reportType: Dv360ReportType.PUBLISHER },
    { signal: payload?.signal },
  );

  if (reportResponse.data) {
    // TODO: move to API
    return { report: mapReportWithLogos(reportResponse.data.report, []), filters: appliedFilters };
  }

  return fallbackValues;
};
